import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import _ from 'lodash';
import { Subject, asyncScheduler } from 'rxjs';
import { observeOn } from 'rxjs/operators';
import { SgComponentHighlightComponent } from '.';
import { EnvironmentService, SgHeapIoService } from '../services';
import { AnnouncementType } from './component-highlight.component';
export const CONTAINER_DATA = new InjectionToken<{}>('CONTAINER_DATA');

type Position = 'right' | 'below' | 'left' | 'above';

type Highlight = {
  id: HighlightsKeys;
  title?: string;
  message: string;
  imageSrc?: string;
  selector: string;
  announcementType?: AnnouncementType;
  hideConfirmButton?: boolean;
  imagePosition?: 'top' | 'bottom';
  autoAcknowledgeTimerMs?: number;
  autoCloseTimerMs?: number;
  hasOverlay?: boolean;
  preferredPosition?: Position;
  showOnce?: boolean;
  isTooltipStyle?: boolean;
};

export interface HighlightConfig {
  id: HighlightsKeys;
  title: string;
  message: string;
  imageSrc?: string;
  announcementType?: AnnouncementType;
  hideConfirmButton?: boolean;
  imagePosition?: 'top' | 'bottom';
  onClose: () => void;
  cancelWithoutAcknowledging: () => void;
  autoAcknowledgeTimerMs?: number;
  autoCloseTimerMs?: number;
  showOnce?: boolean;
  isTooltipStyle?: boolean;
}

type HighlightsKeys =
  | 'example'
  | 'rate_request'
  | 'new_loans'
  | 'refinance'
  | 'lenderRecommendedLendingLimit'
  | 'recommendationApplied';

const HIGHLIGHTS: { [key in HighlightsKeys]: Highlight } = {
  example: {
    id: 'example',
    message: 'Hello this is an example highlight',
    title: 'This is an example title',
    imageSrc: 'https://www.helpguide.org/wp-content/uploads/king-charles-spaniel-resting-head-768.jpg',
    selector: '.sg-highlight-example',
  },
  rate_request: {
    id: 'rate_request',
    message: `We've improved our "Request Rate" feature, allowing you to quickly request a lower rate with just one click.`,
    title: 'Request Rate',
    selector: '.sg-highlight-rate-request:first-of-type',
    announcementType: 'time-saver',
    hideConfirmButton: true,
    preferredPosition: 'below',
  },
  new_loans: {
    id: 'new_loans',
    message: 'It’s never been easier to access your loans’ relevant details and events, Go ahead and check your loans.',
    title: 'New Loans page is live!',
    imageSrc: 'assets/sg-borrower/images/new-loans-announcement.png',
    selector: '.sg-highlight-new-loans a[href="/sg-borrower/loans"]',
    announcementType: 'access',
  },
  refinance: {
    id: 'refinance',
    message: 'Optimise your book today with offers that fit your needs',
    title: 'Refinance has arrived!',
    imageSrc: 'assets/sg-borrower/images/refinance-announcement.png',
    selector: '.sg-highlight-refinance',
    announcementType: 'special-offers',
  },
  lenderRecommendedLendingLimit: {
    id: 'lenderRecommendedLendingLimit',
    message: `This security has reached its availability limit, consider lifting the threshold to unlock hot supply.`,
    selector: '.recommended',
    showOnce: false,
    isTooltipStyle: true,
    hasOverlay: true,
    preferredPosition: 'left',
  },
  recommendationApplied: {
    id: 'recommendationApplied',
    message: `We've applied the recommended lending limit for this security. Review and save when ready.`,
    selector: '[data-recommended="true"]',
    showOnce: false,
    isTooltipStyle: true,
    hasOverlay: true,
    preferredPosition: 'above',
  },
};

@Injectable({ providedIn: 'root' })
export class SgComponentHighlightService {
  private alreadyShownHighlights: Array<string>;

  private changeListener: Subject<any> = new Subject();
  private highlightsListener: Subject<HighlightsKeys> = new Subject();
  private highlightsQueue: Array<HighlightsKeys> = [];

  private currentHighlight: Highlight;
  private currentHighlightElement;

  private currentElementRef;
  private lastElementRefBoundingRect;
  private lastElementRefBorderColor: string;

  private positionValidateInterval;

  private isElementInViewport = false;
  private isElementsCreated = false;

  private overlayRef: OverlayRef;

  private renderer: Renderer2;

  constructor(
    router: Router,
    rendererFactory: RendererFactory2,
    private _injector: Injector,
    private overlay: Overlay,
    private env: EnvironmentService,
    private heap: SgHeapIoService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    this.getShownHighlight();

    window.addEventListener('resize', () => {
      this.changeListener.next(null);
    });
    window.addEventListener(
      'scroll',
      () => {
        this.changeListener.next(null);
      },
      true
    );

    this.changeListener.pipe(observeOn(asyncScheduler)).subscribe(() => {
      if (this.isElementsCreated) {
        this.checkIfElementInViewPort(this.currentElementRef);
        if (this.isElementInViewport) {
          clearInterval(this.positionValidateInterval);
          this.positionValidateInterval = setInterval(() => {
            if (
              !_.isEqual(
                JSON.parse(JSON.stringify(this.lastElementRefBoundingRect)),
                JSON.parse(JSON.stringify(this.currentElementRef.getBoundingClientRect()))
              )
            ) {
              this.renderHighlightContainer(this.currentElementRef);
              this.overlayRef.updatePosition();
              this.updateCaretPosition();
            }
          }, 500);
        } else {
          this.clear();
        }
      } else {
        if (this.highlightsQueue.length === 0) return;
        for (const name of this.highlightsQueue) {
          this.currentHighlight = HIGHLIGHTS[name];
          this.currentElementRef = this.getElementBySelector(this.currentHighlight.selector);
          if (!this.currentElementRef) return;
          this.checkIfElementInViewPort(this.currentElementRef);
          if (this.isElementInViewport) {
            this.isElementsCreated = true;
            this.createHighlight();
            this.createHighlightHint();
            this.changeListener.next(null);
            // this.heap.track('open announcement', {
            //   announcement_name: this.currentHighlight.id
            // });
            break;
          }
        }
      }
    });

    this.highlightsListener.pipe(observeOn(asyncScheduler)).subscribe((highlightId) => {
      if (!this.alreadyShownHighlights.includes(highlightId)) {
        this.highlightsQueue.push(highlightId);
        this.changeListener.next(null);
      }
    });

    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.clear();
        this.highlightsQueue = [];
        this.changeListener.next(null);
      }
    });
  }

  /** Displays the given highlight. If this highlight was already acknoledged by the user, it will not be displayed. */
  show(highlight: HighlightsKeys) {
    this.highlightsListener.next(highlight);
  }

  /** Programatically acknowledge a given highlight, as if the uesr clicked the "Got It" button. */
  acknowledge(highlight: HighlightsKeys) {
    this.addShownHighlight(highlight);
  }

  private getElementBySelector(selector: string) {
    const element = document.querySelector(selector);
    if (!element) return undefined;
    return element;
  }

  private createHighlight() {
    this.currentHighlightElement = this.renderer.createElement('div');
    this.lastElementRefBoundingRect = this.currentElementRef.getBoundingClientRect();
    this.lastElementRefBorderColor = this.currentElementRef.style.borderColor;
    this.currentElementRef.style.borderColor = 'transparent';
    this.renderer.setAttribute(
      this.currentHighlightElement,
      'class',
      this.currentHighlight.hasOverlay ? 'sg-highlight' : 'sg-highlight sg-highlight-no-overlay'
    );
    this.renderHighlightContainer(this.currentElementRef);
    this.renderer.appendChild(document.body, this.currentHighlightElement);
  }

  private renderHighlightContainer(element) {
    this.lastElementRefBoundingRect = element.getBoundingClientRect();
    const { top, left, width, height } = this.lastElementRefBoundingRect;
    this.renderer.setStyle(this.currentHighlightElement, 'width', `${width}px`);
    this.renderer.setStyle(this.currentHighlightElement, 'height', `${height}px`);
    this.renderer.setStyle(this.currentHighlightElement, 'top', `${top}px`);
    this.renderer.setStyle(this.currentHighlightElement, 'left', `${left}px`);
  }

  private checkIfElementInViewPort(element) {
    const { top, left, right, bottom } = element.getBoundingClientRect();
    if (
      top >= 0 &&
      left >= 0 &&
      right <= (window.innerWidth || document.documentElement.clientWidth) &&
      bottom <= (window.innerHeight || document.documentElement.clientHeight)
    ) {
      this.isElementInViewport = true;
    } else {
      this.isElementInViewport = false;
    }
  }

  private createHighlightHint() {
    this.currentHighlight.preferredPosition ??= 'right';
    const { [this.currentHighlight.preferredPosition]: _, ...restPositions } = AllPositions;
    const orderedPositions = [
      AllPositions[this.currentHighlight.preferredPosition],
      ...Object.values(restPositions),
    ] as ConnectedPosition[];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.currentHighlightElement)
      .withPositions(orderedPositions);
    this.overlayRef = this.overlay.create({
      hasBackdrop: this.currentHighlight.hasOverlay !== undefined ? this.currentHighlight.hasOverlay : true,
      panelClass: ['sg-hint-feature', this.currentHighlight.isTooltipStyle ? 'tooltip' : ''],
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    const portal = new ComponentPortal(
      SgComponentHighlightComponent,
      null,
      this.createInjector({
        message: this.currentHighlight.message,
        title: this.currentHighlight.title,
        imageSrc: this.currentHighlight.imageSrc ? this.env.getDeployUrl('/') + this.currentHighlight.imageSrc : null,
        id: this.currentHighlight.id,
        showOnce: this.currentHighlight.showOnce,
        isTooltipStyle: this.currentHighlight.isTooltipStyle,
        announcementType: this.currentHighlight.announcementType,
        hideConfirmButton: this.currentHighlight.hideConfirmButton,
        imagePosition: this.currentHighlight.imagePosition,
        onClose: () => {
          this.clear();
          this.highlightsQueue = this.highlightsQueue.filter((x) => x != this.currentHighlight.id);
          if (this.currentHighlight.showOnce ?? true) {
            this.addShownHighlight(this.currentHighlight.id);
          }
          this.changeListener.next(null);
        },
        autoAcknowledgeTimerMs: this.currentHighlight.autoAcknowledgeTimerMs,
        autoCloseTimerMs: this.currentHighlight.autoCloseTimerMs,
        cancelWithoutAcknowledging: () => {
          this.clear();
          this.highlightsQueue = this.highlightsQueue.filter((x) => x != this.currentHighlight.id);
          this.changeListener.next(null);
        },
      })
    );
    this.overlayRef.attach(portal);
  }

  private createInjector(dataToPass: HighlightConfig): Injector {
    return Injector.create({
      providers: [{ provide: CONTAINER_DATA, useValue: dataToPass }],
      parent: this._injector,
    });
  }

  private updateCaretPosition() {
    const {
      top: elementRefTop,
      left: elementRefLeft,
      width: elementRefWidth,
      height: elementRefHeight,
    } = this.currentElementRef?.getBoundingClientRect();
    const { top: givenElementTop, left: givenElementLeft } = this.overlayRef?.overlayElement?.getBoundingClientRect();
    const DEFAULT_CARET_WIDTH = '15px';

    const caretBaseWidth =
      getComputedStyle(document.querySelector('.sg-hint-feature')).getPropertyValue('--caret-base-width-px').trim() ||
      DEFAULT_CARET_WIDTH;

    const halfCaretBaseWidth = +caretBaseWidth.match(/\d+/g)[0] / 2;

    const leftAndRightPosition = elementRefTop - givenElementTop;
    const topAndBottomPosition = elementRefLeft - givenElementLeft;

    this.overlayRef.overlayElement.style.setProperty(
      '--caret-position-left-right',
      `${leftAndRightPosition + (elementRefHeight / 2 - halfCaretBaseWidth)}px`
    );
    this.overlayRef.overlayElement.style.setProperty(
      '--caret-position-top-bottom',
      `${topAndBottomPosition + (elementRefWidth / 2 - halfCaretBaseWidth)}px`
    );
  }

  private getShownHighlight() {
    this.alreadyShownHighlights = localStorage.getItem('highlights')?.split(',') || [];
  }

  private addShownHighlight(id) {
    this.getShownHighlight();
    this.alreadyShownHighlights.push(id);
    localStorage.setItem('highlights', this.alreadyShownHighlights.join(','));
  }

  clear() {
    if (this.currentHighlightElement) {
      this.renderer.removeChild('body', this.currentHighlightElement);
    }
    if (this.currentElementRef) {
      this.currentElementRef.style.borderColor = this.lastElementRefBorderColor;
    }
    this.overlayRef?.dispose();
    clearInterval(this.positionValidateInterval);
    this.isElementsCreated = false;
    this.isElementInViewport = false;
  }
}

const AllPositions: { [key in Position]: ConnectedPosition } = {
  right: {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 15,
    panelClass: 'left-caret',
  },
  below: {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
    offsetX: 145,
    offsetY: 15,
    panelClass: 'top-caret',
  },
  left: {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -15,
    panelClass: 'right-caret',
  },
  above: {
    originX: 'center',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom',
    offsetX: 145,
    offsetY: -15,
    panelClass: 'bottom-caret',
  },
};
